import { useState, useCallback, useEffect } from "react";
import * as Tone from "tone";
import { Step } from "../types";
import { PATTERN_PRESETS } from "../../../utils/patternPresets";

export const usePatternManagement = (
  stepsCount: number,
  sampler: Tone.Sampler | null,
  sequencerMode?: "chord" | "drone-mono" | "drone-duo"
) => {
  // Determine step mode based on sequencer mode
  const getStepMode = (): "chord" | "drone" => {
    if (sequencerMode === "drone-mono" || sequencerMode === "drone-duo") {
      return "drone";
    }
    return "chord";
  };

  // State for pattern (which steps are active)
  const [pattern, setPattern] = useState<Step[]>(() =>
    Array(stepsCount)
      .fill(null)
      .map(() => ({
        active: false,
        mode: getStepMode(),
      }))
  );

  // State for selected pattern preset
  const [selectedPreset, setSelectedPreset] = useState<string>("empty");

  // Update step modes when sequencer mode changes
  useEffect(() => {
    const newMode = getStepMode();
    setPattern((prevPattern) =>
      prevPattern.map((step) => ({
        ...step,
        mode: newMode,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequencerMode]);

  // Handle pattern expansion/contraction when stepsCount changes
  useEffect(() => {
    setPattern((prevPattern) => {
      const currentLength = prevPattern.length;
      const stepMode = getStepMode();

      if (stepsCount === currentLength) {
        // No change needed
        return prevPattern;
      } else if (stepsCount > currentLength) {
        // Expanding: cycle through existing steps to fill new positions
        const newSteps: Step[] = [];

        for (let i = 0; i < stepsCount; i++) {
          if (i < currentLength) {
            // Keep existing step
            newSteps.push(prevPattern[i]);
          } else {
            // Copy from existing steps (cycling through all available steps)
            const sourceIndex = i % currentLength;
            const sourceStep = prevPattern[sourceIndex];
            newSteps.push({
              ...sourceStep,
              mode: stepMode,
              // Deep copy fxMixes if it exists
              fxMixes: sourceStep.fxMixes
                ? { ...sourceStep.fxMixes }
                : undefined,
            });
          }
        }

        return newSteps;
      } else {
        // Contracting: keep first N steps
        return prevPattern.slice(0, stepsCount);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsCount, sequencerMode]);

  // Toggle a step on or off
  const toggleStep = useCallback(
    (stepIndex: number) => {
      const stepMode = getStepMode();
      setPattern((prevPattern) => {
        const newPattern = [...prevPattern];
        const step = newPattern[stepIndex];

        newPattern[stepIndex] = {
          ...step,
          active: !step.active,
          mode: stepMode,
        };

        return newPattern;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sequencerMode]
  );

  // Apply pattern preset
  const applyPatternPreset = useCallback(
    (presetKey: string) => {
      const samplesReady = !!sampler;
      if (!samplesReady) return;

      const preset = PATTERN_PRESETS[presetKey as keyof typeof PATTERN_PRESETS];
      if (!preset) return;

      const stepMode = getStepMode();
      setPattern((prevPattern) => {
        const newPattern = prevPattern.map((step, index) => {
          const isActive = preset.pattern.includes(index);

          return {
            ...step,
            active: isActive,
            mode: stepMode,
          };
        });

        return newPattern;
      });

      setSelectedPreset(presetKey);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sampler, sequencerMode]
  );

  // Handle preset change
  const handlePresetChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement> | string) => {
      // If e is a string, it was called directly with the preset key
      // Otherwise extract the value from the event
      const presetKey = typeof e === "string" ? e : e.target.value;
      applyPatternPreset(presetKey);
    },
    [applyPatternPreset]
  );

  // Randomize all steps
  const randomizeSequencer = useCallback(() => {
    const samplesReady = !!sampler;
    if (!samplesReady) return;

    const stepMode = getStepMode();
    setPattern((prevPattern) => {
      // We want around 30-40% of steps to be active
      const targetActiveCount = Math.floor(
        stepsCount * (0.3 + Math.random() * 0.1)
      );

      // Randomly select which step indices will be active
      const activeStepIndices: number[] = [];
      while (activeStepIndices.length < targetActiveCount) {
        const randomIndex = Math.floor(Math.random() * stepsCount);
        if (!activeStepIndices.includes(randomIndex)) {
          activeStepIndices.push(randomIndex);
        }
      }

      // Create the new pattern
      const newPattern = prevPattern.map((step, index) => {
        const isActive = activeStepIndices.includes(index);

        return {
          ...step,
          active: isActive,
          mode: stepMode,
        };
      });

      return newPattern;
    });

    // Reset preset selection when randomizing
    setSelectedPreset("empty");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsCount, sampler, sequencerMode]);

  // Update step FX mix value
  const updateStepFXMix = useCallback(
    (stepIndex: number, fxType: string, value: number) => {
      setPattern((prevPattern) => {
        const newPattern = [...prevPattern];
        const step = newPattern[stepIndex];

        newPattern[stepIndex] = {
          ...step,
          fxMixes: {
            ...step.fxMixes,
            [fxType]: value,
          },
        };

        return newPattern;
      });
    },
    []
  );

  // Initialize all active steps with current global value for a given FX
  const initializeStepsWithGlobalValue = useCallback(
    (fxType: string, globalValue: number) => {
      setPattern((prevPattern) => {
        return prevPattern.map((step) => {
          if (!step.active) return step;

          return {
            ...step,
            fxMixes: {
              ...step.fxMixes,
              [fxType]: globalValue,
            },
          };
        });
      });
    },
    []
  );

  // Clear all mapping for a given FX type
  const clearMapping = useCallback((fxType: string) => {
    setPattern((prevPattern) => {
      return prevPattern.map((step) => {
        if (!step.fxMixes) return step;

        const newFxMixes = { ...step.fxMixes };
        delete newFxMixes[fxType as keyof typeof newFxMixes];

        return {
          ...step,
          fxMixes: Object.keys(newFxMixes).length > 0 ? newFxMixes : undefined,
        };
      });
    });
  }, []);

  return {
    pattern,
    selectedPreset,
    toggleStep,
    applyPatternPreset,
    handlePresetChange,
    randomizeSequencer,
    updateStepFXMix,
    initializeStepsWithGlobalValue,
    clearMapping,
    setPattern, // Expose setPattern for preset loading
  };
};
